import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import type { DataGridColumn, DataGridColumnSection } from '../data-grid.types';

/** Vertical gap between the Columns button and the popover. */
const POPOVER_ANCHOR_GAP_PX: number = 8;

/** Focusable controls inside the columns dialog. */
const FOCUSABLE_SELECTOR: string =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Toolbar Columns button and visibility popover for standalone or panel-hosted grids.
 */
@Component({
  selector: 'd-data-grid-columns-popover',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslocoPipe],
  templateUrl: './data-grid-columns-popover.component.html',
  styleUrl: './data-grid-columns-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)'
  }
})
export class DataGridColumnsPopoverComponent {
  /** Columns button used to anchor the fixed popover. */
  private readonly columnsButton: Signal<ElementRef<HTMLButtonElement> | undefined> = viewChild(
    'columnsButton',
    { read: ElementRef }
  );

  /** Dialog panel that receives focus when the popover opens. */
  private readonly popoverDialog: Signal<ElementRef<HTMLElement> | undefined> = viewChild('columnsPopover', {
    read: ElementRef
  });

  /** Column sections shown in the popover. */
  public sections: InputSignal<Array<DataGridColumnSection>> = input<Array<DataGridColumnSection>>(
    new Array<DataGridColumnSection>()
  );

  /** Fields currently hidden by the user. */
  public hiddenColumns: InputSignal<Set<string>> = input<Set<string>>(new Set<string>());

  /** Emits the next hidden-field set after a toggle or reset. */
  public hiddenColumnsChange: OutputEmitterRef<Set<string>> = output<Set<string>>();

  /** Whether the popover is open. */
  protected open: WritableSignal<boolean> = signal<boolean>(false);

  /** Collapsed section ids in the popover. */
  protected collapsedSections: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());

  /** Popover `top` in viewport pixels. */
  protected popoverTopPx: WritableSignal<number> = signal<number>(0);

  /** Popover `right` in viewport pixels. */
  protected popoverRightPx: WritableSignal<number> = signal<number>(0);

  /**
   * Opens or closes the popover. Positions the overlay from the button when opening.
   */
  protected toggleOpen(): void {
    if (this.open()) {
      this.close();
      return;
    }
    this.positionPopover();
    this.open.set(true);
    this.scheduleFocusPopover();
  }

  /** Closes the popover and returns focus to the Columns button. */
  protected close(): void {
    if (!this.open()) {
      return;
    }
    this.open.set(false);
    this.scheduleFocusColumnsButton();
  }

  /**
   * Handles Escape (close) and Tab (focus trap) while the popover is open.
   *
   * @param event - Document keydown event.
   */
  protected onDocumentKeydown(event: Event): void {
    const keyboardEvent: KeyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Escape') {
      this.onDocumentEscape(keyboardEvent);
      return;
    }
    if (keyboardEvent.key === 'Tab') {
      this.onDocumentTab(keyboardEvent);
    }
  }

  /**
   * Closes the popover when Escape is pressed while it is open.
   *
   * @param event - Document keydown event.
   */
  protected onDocumentEscape(event: Event): void {
    if (!this.open()) {
      return;
    }
    event.preventDefault();
    this.close();
  }

  /**
   * Keeps Tab and Shift+Tab inside the open dialog.
   *
   * @param event - Document Tab keydown event.
   */
  protected onDocumentTab(event: KeyboardEvent): void {
    if (!this.open()) {
      return;
    }
    const dialog: HTMLElement | undefined = this.popoverDialog()?.nativeElement;
    if (dialog === undefined) {
      return;
    }
    const focusable: Array<HTMLElement> = this.collectFocusable(dialog);
    if (focusable.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first: HTMLElement = focusable[0];
    const last: HTMLElement = focusable[focusable.length - 1];
    const active: Element | null = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Toggles a column's visibility and emits the updated hidden set.
   *
   * @param field - Column field name.
   */
  protected toggleColumn(field: string): void {
    const next: Set<string> = new Set<string>(this.hiddenColumns());
    if (next.has(field)) {
      next.delete(field);
    } else {
      next.add(field);
    }
    this.hiddenColumnsChange.emit(next);
  }

  /**
   * Returns true when the column is currently visible.
   *
   * @param field - Column field name.
   * @returns True when the field is not in the hidden set.
   */
  protected isColumnVisible(field: string): boolean {
    return !this.hiddenColumns().has(field);
  }

  /**
   * Counts visible columns in a popover section.
   *
   * @param section - Popover section.
   * @returns Visible column count.
   */
  protected visibleCount(section: DataGridColumnSection): number {
    const hidden: Set<string> = this.hiddenColumns();
    return section.columns.filter((column: DataGridColumn): boolean => !hidden.has(column.field)).length;
  }

  /** Shows every column and emits an empty hidden set. */
  protected resetColumns(): void {
    this.hiddenColumnsChange.emit(new Set<string>());
  }

  /**
   * Expands or collapses a popover section.
   *
   * @param sectionId - Stable section id.
   */
  protected toggleSection(sectionId: string): void {
    this.collapsedSections.update((current: Set<string>): Set<string> => {
      const next: Set<string> = new Set<string>(current);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }

  /**
   * Returns true when the section body is expanded.
   *
   * @param sectionId - Stable section id.
   * @returns True when the section is not collapsed.
   */
  protected isSectionExpanded(sectionId: string): boolean {
    return !this.collapsedSections().has(sectionId);
  }

  /**
   * Builds a stable element id for a popover section body.
   *
   * @param sectionId - Stable section id.
   * @returns DOM id for `aria-controls`.
   */
  protected sectionBodyId(sectionId: string): string {
    return `dg-columns-section-${sectionId}`;
  }

  /**
   * Builds a stable element id for a column label.
   *
   * @param field - Column field name.
   * @returns DOM id for `aria-labelledby`.
   */
  protected columnLabelId(field: string): string {
    return `dg-columns-label-${field}`;
  }

  /**
   * Builds a stable element id for a column visibility checkbox.
   *
   * @param field - Column field name.
   * @returns DOM id for the label `for` attribute.
   */
  protected columnInputId(field: string): string {
    return `dg-columns-input-${field}`;
  }

  /** Anchors the fixed popover below the Columns button. */
  private positionPopover(): void {
    const button: HTMLButtonElement | undefined = this.columnsButton()?.nativeElement;
    if (button === undefined) {
      return;
    }
    const rect: DOMRect = button.getBoundingClientRect();
    this.popoverTopPx.set(rect.bottom + POPOVER_ANCHOR_GAP_PX);
    this.popoverRightPx.set(window.innerWidth - rect.right);
  }

  /** Focuses the dialog after it is rendered. */
  private scheduleFocusPopover(): void {
    queueMicrotask((): void => {
      this.popoverDialog()?.nativeElement.focus();
    });
  }

  /** Restores focus to the Columns button after the dialog closes. */
  private scheduleFocusColumnsButton(): void {
    queueMicrotask((): void => {
      this.columnsButton()?.nativeElement.focus();
    });
  }

  /**
   * Collects tabbable elements inside the dialog.
   *
   * @param dialog - Open popover root.
   * @returns Focusable elements in document order.
   */
  private collectFocusable(dialog: HTMLElement): Array<HTMLElement> {
    return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  }
}
